import { supabase } from '../lib/supabase';

// ============================================================================
// REAL-TIME SUBSCRIPTION MANAGER
// ============================================================================

class RealtimeSubscriptionManager {
  constructor() {
    this.channels = new Map();
  }

  /**
   * Subscribe to project changes for a specific user
   * @param {string} userId - User ID to filter projects
   * @param {function} callback - Callback function to handle changes
   * @returns {object} Channel object for cleanup
   */
  subscribeToProjects(userId, callback) {
    const channelName = `projects_${userId}`;
    
    // Remove existing channel if any
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            data: payload?.new,
            old: payload?.old
          });
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to saved designs for a specific project
   * @param {string} projectId - Project ID to filter designs
   * @param {function} callback - Callback function to handle changes
   * @returns {object} Channel object for cleanup
   */
  subscribeToDesigns(projectId, callback) {
    const channelName = `designs_${projectId}`;
    
    // Remove existing channel if any
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'saved_designs',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            data: payload?.new,
            old: payload?.old
          });
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to generation metadata for a specific user
   * @param {string} userId - User ID to filter generation activities
   * @param {function} callback - Callback function to handle changes
   * @returns {object} Channel object for cleanup
   */
  subscribeToGenerationMetadata(userId, callback) {
    const channelName = `generation_${userId}`;
    
    // Remove existing channel if any
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'generation_metadata',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            data: payload?.new,
            old: payload?.old
          });
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to user profile changes
   * @param {string} userId - User ID to monitor
   * @param {function} callback - Callback function to handle changes
   * @returns {object} Channel object for cleanup
   */
  subscribeToUserProfile(userId, callback) {
    const channelName = `profile_${userId}`;
    
    // Remove existing channel if any
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${userId}`
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            data: payload?.new,
            old: payload?.old
          });
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Subscribe to all project changes (for collaborative features)
   * @param {function} callback - Callback function to handle changes
   * @returns {object} Channel object for cleanup
   */
  subscribeToAllProjects(callback) {
    const channelName = 'all_projects';
    
    // Remove existing channel if any
    this.unsubscribe(channelName);

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects'
        },
        (payload) => {
          callback({
            event: payload?.eventType,
            data: payload?.new,
            old: payload?.old
          });
        }
      )?.subscribe();

    this.channels?.set(channelName, channel);
    return channel;
  }

  /**
   * Unsubscribe from a specific channel
   * @param {string} channelName - Name of the channel to unsubscribe
   */
  unsubscribe(channelName) {
    const channel = this.channels?.get(channelName);
    if (channel) {
      supabase?.removeChannel(channel);
      this.channels?.delete(channelName);
    }
  }

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll() {
    this.channels?.forEach((channel) => {
      supabase?.removeChannel(channel);
    });
    this.channels?.clear();
  }

  /**
   * Get active channel count
   * @returns {number} Number of active channels
   */
  getActiveChannelCount() {
    return this.channels?.size;
  }
}

// Export singleton instance
export const realtimeManager = new RealtimeSubscriptionManager();

// ============================================================================
// CONVENIENCE HOOKS FOR REACT COMPONENTS
// ============================================================================

/**
 * Helper function to convert snake_case to camelCase
 */
function convertKeysToCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(convertKeysToCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = convertKeysToCamelCase(obj?.[key]);
    return acc;
  }, {});
}

/**
 * Subscribe to real-time project updates
 * @param {string} userId - User ID
 * @param {function} onInsert - Callback for new projects
 * @param {function} onUpdate - Callback for updated projects
 * @param {function} onDelete - Callback for deleted projects
 * @returns {function} Cleanup function
 */
export function subscribeToProjectUpdates(userId, { onInsert, onUpdate, onDelete }) {
  const channel = realtimeManager?.subscribeToProjects(userId, ({ event, data, old }) => {
    const camelData = convertKeysToCamelCase(data);
    const camelOld = convertKeysToCamelCase(old);

    switch (event) {
      case 'INSERT':
        onInsert?.(camelData);
        break;
      case 'UPDATE':
        onUpdate?.(camelData, camelOld);
        break;
      case 'DELETE':
        onDelete?.(camelOld);
        break;
      default:
        break;
    }
  });

  return () => realtimeManager?.unsubscribe(`projects_${userId}`);
}

/**
 * Subscribe to real-time design updates for a project
 * @param {string} projectId - Project ID
 * @param {function} onInsert - Callback for new designs
 * @param {function} onUpdate - Callback for updated designs
 * @param {function} onDelete - Callback for deleted designs
 * @returns {function} Cleanup function
 */
export function subscribeToDesignUpdates(projectId, { onInsert, onUpdate, onDelete }) {
  const channel = realtimeManager?.subscribeToDesigns(projectId, ({ event, data, old }) => {
    const camelData = convertKeysToCamelCase(data);
    const camelOld = convertKeysToCamelCase(old);

    switch (event) {
      case 'INSERT':
        onInsert?.(camelData);
        break;
      case 'UPDATE':
        onUpdate?.(camelData, camelOld);
        break;
      case 'DELETE':
        onDelete?.(camelOld);
        break;
      default:
        break;
    }
  });

  return () => realtimeManager?.unsubscribe(`designs_${projectId}`);
}

/**
 * Subscribe to real-time generation activity updates
 * @param {string} userId - User ID
 * @param {function} onInsert - Callback for new generation activities
 * @param {function} onUpdate - Callback for updated generation activities
 * @returns {function} Cleanup function
 */
export function subscribeToGenerationUpdates(userId, { onInsert, onUpdate }) {
  const channel = realtimeManager?.subscribeToGenerationMetadata(userId, ({ event, data }) => {
    const camelData = convertKeysToCamelCase(data);

    switch (event) {
      case 'INSERT':
        onInsert?.(camelData);
        break;
      case 'UPDATE':
        onUpdate?.(camelData);
        break;
      default:
        break;
    }
  });

  return () => realtimeManager?.unsubscribe(`generation_${userId}`);
}

/**
 * Subscribe to user profile updates (for credit changes, etc.)
 * @param {string} userId - User ID
 * @param {function} onUpdate - Callback for profile updates
 * @returns {function} Cleanup function
 */
export function subscribeToProfileUpdates(userId, onUpdate) {
  const channel = realtimeManager?.subscribeToUserProfile(userId, ({ data }) => {
    const camelData = convertKeysToCamelCase(data);
    onUpdate?.(camelData);
  });

  return () => realtimeManager?.unsubscribe(`profile_${userId}`);
}

export default realtimeManager;